import { RpcError, StatusCode } from 'grpc-web'
import type { AlertFactory } from '@/features/shell'
import { type Bundle } from '@/features/admin/bundle/Bundle'
import type { Id } from '@/infra/id'

/**
 * How many skipped bundles to name in a partial-success alert before
 * summarizing the rest as "+N more".
 */
const maxNamedBundles = 5

export interface BulkActionResultMessages {
  success: (succeededCount: number) => string
  partialTitle: string
  partial: (succeededCount: number, skipped: Bundle[]) => string
}

/**
 * Shows a single success or partial-success alert for a bulk action whose
 * response reports skipped bundles by id. Both the price-edit and archive
 * responses share this errors-only shape: any sent bundle absent from the
 * skip set succeeded.
 */
export function showBulkActionResult(
  newAlert: AlertFactory,
  sent: Bundle[],
  skippedIds: Set<Id>,
  messages: BulkActionResultMessages,
) {
  const skipped = sent.filter((bundle) => skippedIds.has(bundle.id))
  const succeededCount = sent.length - skipped.length

  if (skipped.length === 0) {
    newAlert()
      .withState('success')
      .saying('Success', messages.success(succeededCount))
      .autoDismiss()
      .show()
    return
  }

  newAlert()
    .withState('error')
    .saying(messages.partialTitle, messages.partial(succeededCount, skipped))
    .autoDismiss()
    .show()
}

/**
 * Shows the error alert for a failed bulk action. `PermissionDenied` and
 * `InvalidArgument` get specific messaging; anything else is shown as a
 * generic failure.
 */
export function showBulkActionError(
  newAlert: AlertFactory,
  error: unknown,
  permissionMessage: string,
  genericMessage: string,
) {
  if (
    error instanceof RpcError &&
    error.code === StatusCode.PERMISSION_DENIED
  ) {
    newAlert()
      .withState('error')
      .saying('Error', permissionMessage)
      .autoDismiss()
      .show()
    return
  }

  if (error instanceof RpcError && error.code === StatusCode.INVALID_ARGUMENT) {
    newAlert()
      .withState('error')
      .saying('Error', error.message)
      .autoDismiss()
      .show()
    return
  }

  newAlert()
    .withState('error')
    .saying('Error', genericMessage)
    .autoDismiss()
    .show()
}

export function pluralizeBundles(count: number) {
  return count === 1 ? 'bundle' : 'bundles'
}

export function formatBundleNames(bundles: Bundle[]) {
  const names = bundles.slice(0, maxNamedBundles).map((bundle) => bundle.name)
  const remaining = bundles.length - names.length

  return remaining > 0
    ? `${names.join(', ')} +${remaining} more`
    : names.join(', ')
}
